// sourceReadStatus — the client side of a source's reading status (FR-3.3,
// FR-3.4, FR-3.6). The server projects it (`Resource.ReadStatus.
// sourceReadStatus`, on every `/state` source row); this module parses it at
// the boundary, looks it up in the live Library, and maps it to the chip
// vocabulary. Pure. "Uploading" is not here: before the upload answers there
// is no source yet, only the client's own ingest batch.

export type { SourceReadStatus };
export { chipStatusOf, parseReadStatus, readStatusOf };

import type * as Core from "@bilda/core";
import type { ComposerAttachmentStatus } from "../components/Composer/PowerComposer/types.js";
import type { Library } from "./ports/workspace.js";
import { fields, num, str } from "./wireShape.js";

type SourceReadStatus = Core.Resource.ReadStatus.SourceReadStatus;
type Reason = Core.Resource.ReadStatus.TranscriptionFailureReason;

const REASONS: readonly Reason[] = [
    "original_file_unavailable",
    "pages_not_renderable",
    "recognition_unavailable",
];

const isReason = (raw: string | undefined): raw is Reason =>
    REASONS.some((r) => r === raw);

// Anything malformed (absent, an older server, shape drift) reads as
// `undefined`: the surface then shows no status rather than a guessed one.
const parseReadStatus = (raw: unknown): SourceReadStatus | undefined => {
    const f = fields(raw);
    switch (str(f?.get("type"))) {
        case "source_read_ready":
            return { type: "source_read_ready" };
        case "source_reading_scanned_pages": {
            const pageCount = num(f?.get("pageCount"));
            return pageCount === undefined
                ? undefined
                : { type: "source_reading_scanned_pages", pageCount };
        }
        case "source_read_failed": {
            const reason = str(f?.get("reason"));
            return isReason(reason)
                ? { type: "source_read_failed", reason }
                : undefined;
        }
        default:
            return undefined;
    }
};

// A source's status in the live Library (refetched as the log head moves).
const readStatusOf = (
    library: Library | null,
    sourceId: string,
): SourceReadStatus | undefined =>
    library?.sources.find((s) => s.id === sourceId)?.readStatus;

// The chip decoration for a landed source: none once it is readable.
const chipStatusOf = (
    status: SourceReadStatus | undefined,
): ComposerAttachmentStatus | undefined => {
    switch (status?.type) {
        case "source_reading_scanned_pages":
            return "reading";
        case "source_read_failed":
            return "read_failed";
        default:
            return undefined;
    }
};
